import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Typography, Button, Stack, Divider } from '@mui/material';
import PolicyIcon from '@mui/icons-material/Policy';
import { getStaticFileUrl } from '../utils/staticUtils';
import ArticleIcon from '@mui/icons-material/Article';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import IconButton from '@mui/material/IconButton';
import { useNexaStayThemeContext } from './NexaStayThemeContext';
import TermsPolicyDialog from './TermsPolicyDialog';
import { resolveNexaStayHotelId } from './useNexaStayBasePath';

export default function NexaStayFooter() {
  const { hotelId: routeHotelId } = useParams();
  const hotelId = resolveNexaStayHotelId(routeHotelId);
  const { theme } = useNexaStayThemeContext();
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [termsDialogTab, setTermsDialogTab] = useState(null);
  const accent = theme.accent;
  const footerBg = theme.headerFooterBg ?? theme.paper;
  const footerShadow =
    theme.mode === 'dark'
      ? '0 -8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.06)'
      : '0 -8px 32px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)';

  return (
    <Box
      component="footer"
      sx={{
        mt: 0,
        position: 'relative',
        overflow: 'hidden',
        borderTop: `1px solid ${theme.borderAccent}`,
        boxShadow: footerShadow,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          height: '100%',
          background: footerBg,
          pointerEvents: 'none',
          zIndex: 0,
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3 }, py: 5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 4, md: 6 }} alignItems={{ md: 'flex-start' }} justifyContent="space-between">
          <Box sx={{ flex: { md: '0 0 240px' } }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <Box
                component="img"
                src={getStaticFileUrl(theme.mode === 'dark' ? 'topbar/logo_ed_dark.png' : 'topbar/logo_ed.png')}
                alt="RoomNexa"
                sx={{
                  height: 40,
                  width: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </Stack>
            <Typography variant="body2" sx={{ color: theme.textSecondary, lineHeight: 1.7 }}>
              Your trusted platform for seamless hotel bookings. Discover comfort, convenience, and unforgettable stays.
            </Typography>
          </Box>

          <Box sx={{ flex: 1, maxWidth: 360 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <PolicyIcon sx={{ color: accent, fontSize: 22 }} />
              <Typography variant="subtitle1" sx={{ color: theme.textPrimary, fontWeight: 600 }}>
                Hotel Terms & Conditions
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: theme.textSecondary, lineHeight: 1.8, mb: 1.5 }}>
              By making a booking, you agree to our cancellation policy, check-in/out times, and house rules. Rates include applicable taxes.
            </Typography>
            <Button
              size="small"
              endIcon={<ArticleIcon sx={{ fontSize: 18 }} />}
              onClick={() => {
                if (hotelId) {
                  setTermsDialogTab(null);
                  setTermsDialogOpen(true);
                }
              }}
              component={hotelId ? 'button' : Link}
              to={hotelId ? undefined : '/terms'}
              sx={{
                color: accent,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: `${accent}18` },
              }}
            >
              View Full Terms
            </Button>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ color: theme.textPrimary, fontWeight: 600, mb: 2 }}>
              Quick Links
            </Typography>
            <Stack spacing={1}>
              {[
                { label: 'Privacy Policy', policyType: 'privacy_policy' },
                { label: 'Terms & Policies', policyType: 'terms_and_conditions' },
                { label: 'Cancellation Policy', policyType: 'cancellation_policy' },
              ].map((link) => (
                <Button
                  key={link.label}
                  size="small"
                  onClick={() => {
                    if (hotelId) {
                      setTermsDialogTab(link.policyType);
                      setTermsDialogOpen(true);
                    }
                  }}
                  component={hotelId ? 'button' : Link}
                  to={hotelId ? undefined : '/terms'}
                  sx={{
                    color: theme.textSecondary,
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    '&:hover': { color: accent },
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1}>
            <IconButton component="a" href="https://linkedin.com" target="_blank" rel="noopener noreferrer" sx={{ color: theme.textMuted, border: `1px solid ${theme.border}`, '&:hover': { color: accent, borderColor: accent, bgcolor: `${accent}18` } }}>
              <LinkedInIcon fontSize="small" />
            </IconButton>
            <IconButton component="a" href="https://twitter.com" target="_blank" rel="noopener noreferrer" sx={{ color: theme.textMuted, border: `1px solid ${theme.border}`, '&:hover': { color: accent, borderColor: accent, bgcolor: `${accent}18` } }}>
              <TwitterIcon fontSize="small" />
            </IconButton>
            <IconButton component="a" href="https://instagram.com" target="_blank" rel="noopener noreferrer" sx={{ color: theme.textMuted, border: `1px solid ${theme.border}`, '&:hover': { color: accent, borderColor: accent, bgcolor: `${accent}18` } }}>
              <InstagramIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        <Divider sx={{ borderColor: theme.border, my: 4 }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography variant="caption" sx={{ color: theme.textMuted }}>
            © {new Date().getFullYear()} RoomNexa. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              size="small"
              onClick={() => (hotelId ? setTermsDialogOpen(true) : null)}
              component={hotelId ? 'button' : Link}
              to={hotelId ? undefined : '/terms'}
              sx={{ color: theme.textMuted, textTransform: 'none', minWidth: 0, p: 0 }}
            >
              Terms of Service
            </Button>
            <Button
              size="small"
              onClick={() => (hotelId ? setTermsDialogOpen(true) : null)}
              component={hotelId ? 'button' : Link}
              to={hotelId ? undefined : '/terms'}
              sx={{ color: theme.textMuted, textTransform: 'none', minWidth: 0, p: 0 }}
            >
              Privacy
            </Button>
          </Stack>
        </Stack>
      </Box>

      <TermsPolicyDialog
        open={termsDialogOpen}
        onClose={() => { setTermsDialogOpen(false); setTermsDialogTab(null); }}
        hotelId={hotelId}
        selectedPolicyType={termsDialogTab}
      />
    </Box>
  );
}
